import { useEffect, useRef, useState } from "react";
import { useForm } from "@mantine/form";
import { useSnackbar } from "notistack";
import { z } from "zod";

import { getOrgInfo, updateOrgInfo } from "../../api";
import Loader from "../../components/Loader";
import { usePermissions } from "../../hooks/usePermissions";
import validateAsync from "@/utils/ZodHelpers/validateAsync";
import { FormWrapper } from "@/components/Forms/components/MonolithFormComponents";
import {
  Button,
  Divider,
  Grid,
  TextInput,
  styled,
} from "@monolith-forensics/monolith-ui";

import { Organization } from "./System";

interface OrgFormData {
  name: string;
  address?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  phone?: string;
  email?: string;
  website?: string;
}

const orgInfoValidationSchema = z.object({
  name: z
    .string({
      required_error: "Organization name is required",
    })
    .min(1, { message: "Organization name is required" }),
  address: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  zipcode: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  email: z
    .union([z.string().email(), z.string().max(0)])
    .nullable()
    .optional(),
  website: z.string().nullable().optional(),
});

const OrgInfoSettings = () => {
  const { hasPermission } = usePermissions();
  const [orgInfo, setOrgInfo] = useState<Organization | null>(null);
  const logoImg = useRef<HTMLImageElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const form = useForm<OrgFormData>({
    mode: "uncontrolled",
    initialValues: {
      name: "",
      address: "",
      city: "",
      state: "",
      zipcode: "",
      phone: "",
      email: "",
      website: "",
    },
  });

  useEffect(() => {
    getOrgInfo().then((result) => {
      let arrayBufferView = new Uint8Array(result.image.data);
      let blob = new Blob([arrayBufferView]);
      let urlCreator = window.URL || window.webkitURL;
      let imageUrl = urlCreator.createObjectURL(blob);

      const orgData = { ...result, image: imageUrl };
      setOrgInfo(orgData);

      // Populate form with fetched data
      form.setValues({
        name: orgData.name || "",
        address: orgData.address || "",
        city: orgData.city || "",
        state: orgData.state || "",
        zipcode: orgData.zipcode || "",
        phone: orgData.phone || "",
        email: orgData.email || "",
        website: orgData.website || "",
      });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    const formData = form.getValues();

    const validationResult = await validateAsync(orgInfoValidationSchema)(
      formData
    );

    if (validationResult?.hasErrors) {
      console.error("Validation Errors", validationResult?.errors);
      form.setErrors(validationResult?.errors);
      return;
    }

    try {
      await updateOrgInfo({
        name: formData.name,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zipcode: formData.zipcode,
        phone: formData.phone,
        email: formData.email,
        website: formData.website,
      });

      enqueueSnackbar(`Organization Info Updated`, {
        variant: "success",
      });

      // Reset touched state after successful update
      form.resetTouched();
    } catch (error) {
      console.error("Error updating org info:", error);
      enqueueSnackbar(`Error updating organization info`, {
        variant: "error",
      });
    }
  };

  const handleUpdateLogo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const errors: string[] = [];

      // Collect all validation errors
      if (!file.type.includes("image")) {
        errors.push("File is not an image");
      }

      if (file.type !== "image/jpeg" && file.type !== "image/png") {
        errors.push("Logo image must be in JPEG or PNG file format");
      }

      if (file.size > 100000) {
        errors.push("File is too large, must be less than 100KB");
      }

      // If there are any errors, display them all and return
      if (errors.length > 0) {
        errors.forEach((error) => {
          enqueueSnackbar(error, {
            variant: "error",
          });
        });
        return;
      }

      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = () => {
        if (logoImg.current) {
          // Check if logoImg.current is not null
          logoImg.current.src = reader.result as string; // Cast to string
          updateOrgInfo({
            image: (reader.result as string).split(";base64,")[1],
          })
            .then(() => {
              enqueueSnackbar(`Organization Logo Updated`, {
                variant: "success",
              });
            })
            .catch((error) => {
              enqueueSnackbar(`Error uploading logo: ${error}`, {
                variant: "error",
              });
            });
        }
      };
    }
  };

  if (!orgInfo) return <Loader />;

  return (
    <SectionContainer>
      <div style={{ display: "flex", justifyContent: "center", width: 650 }}>
        <div style={{ width: "90%" }}>
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: "large", marginBottom: "20px" }}>
              Organization Logo
            </div>
            <div style={{ textAlign: "center" }}>
              <img
                ref={logoImg}
                src={orgInfo.image}
                height={100}
                alt="Organization Logo"
              />
            </div>
            <div
              style={{ display: "flex", justifyContent: "end", marginTop: 20 }}
            >
              <input
                accept="image/*"
                style={{ display: "none" }}
                ref={fileInputRef}
                type="file"
                onChange={handleUpdateLogo}
                disabled={!hasPermission()}
              />
              <Button
                size="xs"
                variant="contained"
                color="primary"
                disabled={!hasPermission()}
                onClick={() => fileInputRef.current?.click()}
              >
                Update Logo
              </Button>
            </div>
          </div>
          <Divider style={{ margin: "20px 0px" }} />
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: "large", marginBottom: "20px" }}>
              Organization Details
            </div>
            <FormWrapper>
              <Grid col={2}>
                <TextInput
                  size="sm"
                  variant="outlined"
                  label="Name"
                  required={true}
                  placeholder="Enter organization name"
                  description="The name of the organization"
                  key={form.key("name")}
                  {...form.getInputProps("name")}
                />
                <TextInput
                  size="sm"
                  variant="outlined"
                  label="Phone"
                  placeholder="Enter phone number"
                  description="Organization phone number"
                  key={form.key("phone")}
                  {...form.getInputProps("phone")}
                />
                <TextInput
                  size="sm"
                  variant="outlined"
                  label="Email"
                  placeholder="Enter email address"
                  description="Organization email address"
                  key={form.key("email")}
                  {...form.getInputProps("email")}
                />
                <TextInput
                  size="sm"
                  variant="outlined"
                  label="Website"
                  placeholder="Enter website URL"
                  description="Organization website"
                  key={form.key("website")}
                  {...form.getInputProps("website")}
                />
                <TextInput
                  size="sm"
                  variant="outlined"
                  label="Address"
                  placeholder="Enter address"
                  description="Organization address"
                  key={form.key("address")}
                  {...form.getInputProps("address")}
                />
                <TextInput
                  size="sm"
                  variant="outlined"
                  label="City"
                  placeholder="Enter city"
                  description="Organization city"
                  key={form.key("city")}
                  {...form.getInputProps("city")}
                />
                <TextInput
                  size="sm"
                  variant="outlined"
                  label="State"
                  placeholder="Enter state/province"
                  description="Organization state or province"
                  key={form.key("state")}
                  {...form.getInputProps("state")}
                />
                <TextInput
                  size="sm"
                  variant="outlined"
                  label="Postal Code"
                  placeholder="Enter postal code"
                  description="Organization postal code"
                  key={form.key("zipcode")}
                  {...form.getInputProps("zipcode")}
                />
              </Grid>
            </FormWrapper>
            <div
              style={{ display: "flex", justifyContent: "end", marginTop: 20 }}
            >
              <Button
                variant="contained"
                size="xs"
                color="primary"
                onClick={handleSubmit}
                disabled={!hasPermission() || !form.isTouched()}
              >
                Save Changes
              </Button>
            </div>
          </div>
          <Divider style={{ margin: "20px 0px" }} />
        </div>
      </div>
    </SectionContainer>
  );
};

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  overflow-y: auto;
`;

export default OrgInfoSettings;
